/**
 * A range of days, as a person picks it, and as the instants it means.
 *
 * `ArFilterDateRange` holds ISO days, both ends inclusive, in the viewer's calendar. What compares
 * against a timestamp is two instants, and the translation is where the quiet mistake lives: «hasta
 * el 24» has to include the whole of the 24th, so the upper bound is the next local midnight and
 * exclusive — not midnight of the 24th, which would leave that day out.
 */

/** What `ArFilterDateRange` holds: ISO `yyyy-mm-dd` days, both inclusive; `null` is an open end. */
export interface DayRange {
  from: string | null;
  to: string | null;
}

/** Local midnight of an ISO day, as an instant. `offsetDays` moves it. */
function midnight(isoDay: string, offsetDays = 0): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay);
  if (!match) return undefined;

  const [, year, month, day] = match;

  return new Date(Number(year), Number(month) - 1, Number(day) + offsetDays).toISOString();
}

/**
 * The range as ISO instants: `from` inclusive, `to` exclusive (the midnight after the last day).
 *
 * An open end, or one that is not a day, is left out rather than sent as something else: a bound
 * nobody could read filters nothing.
 */
export function dayRangeToInstants(range: DayRange | null | undefined): {
  from?: string;
  to?: string;
} {
  const instants: { from?: string; to?: string } = {};

  const from = range?.from ? midnight(range.from) : undefined;
  const to = range?.to ? midnight(range.to, 1) : undefined;
  if (from) instants.from = from;
  if (to) instants.to = to;

  return instants;
}

/**
 * Whether a moment falls inside the range, for a list filtered on screen.
 *
 * The same bounds `dayRangeToInstants` sends upstream, so a list filtered here and one filtered by
 * the server agree on the edges. With no bound everything is inside; with one, a row with no date
 * is outside — «subido en septiembre» cannot be said of something whose date nobody knows.
 */
export function withinDayRange(
  date: Date | null | undefined,
  range: DayRange | null | undefined,
): boolean {
  const { from, to } = dayRangeToInstants(range);
  if (!from && !to) return true;

  const time = date?.getTime();
  if (time === undefined || Number.isNaN(time)) return false;

  if (from && time < Date.parse(from)) return false;
  if (to && time >= Date.parse(to)) return false;

  return true;
}
